<script setup lang="ts">
import type { ListboxPassThroughOptions } from 'primevue/listbox';
import {
  computed,
  ref,
} from 'vue';

import type { IFormPlainDataListboxItem, IFormPlainDataListboxPropsChangeState } from '@modules/forms/types';
import VSplitter from '@components/splitter/VSplitter/VSplitter.vue';
import { useDevice } from '@modules/core/composables/device';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import { TRANSLATION_GROUP_DROPDOWN } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_INTERFACE_LANGUAGES } from '@modules/i18n/constants';
import { watchImmediate } from '@vueuse/core';

import type { IAffiliateAutoApproveLocale } from '../../types';
import type { IAutoApproveFilterEmits, IAutoApproveFilterProps } from './types';
import { TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE } from '../../constants';

const props = defineProps<IAutoApproveFilterProps>();

const emit = defineEmits<IAutoApproveFilterEmits>();

const { isDesktop } = useDevice();
const { getTranslation } = useI18n();

const localeFilterQuery = ref<string>();
const selectedLanguageId = ref<number>();

const listboxPt: ListboxPassThroughOptions = {
  header: {
    class: 'p-0',
  },
  option: {
    class: '!p-2 min-h-[40px]',
  },
  optionGroup: {
    class: 'truncate',
  },
  listContainer: {
    class: 'min-h-[240px]',
  },
};

const filteredSearchAdmin = computed<string | undefined>(() => {
  return props?.admins?.find(({ id }) => id === props.filterAdminId)?.value;
});

const translatedLanguages = computed<IAffiliateAutoApproveLocale[]>(() => (
  props?.locales?.map(({ id, value }) => ({
    id,
    value: getTranslation(TRANSLATION_GROUP_INTERFACE_LANGUAGES, `LANGUAGES_${value}`).value,
  })) ?? []
));

const filteredLanguages = computed(() => {
  const query = localeFilterQuery.value;

  return query
    ? translatedLanguages.value.filter((item) => item.value.toLowerCase().includes(query.toLowerCase()))
    : translatedLanguages.value;
});

const groupedLanguages = computed(() => {
  const adminId = props.filterAdminId;
  const languages = filteredLanguages.value;

  if (!adminId) {
    return languages;
  }
  const selected = languages.filter(({ id }) => props.localizedAdmins?.[id]?.includes(adminId));
  const unselected = languages.filter(({ id }) => !props.localizedAdmins?.[id]?.includes(adminId));

  if (!selected.length && !unselected.length) {
    return [];
  }

  return [
    {
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE, 'SELECTED_GROUP_LABEL'),
      items: selected,
    },
    {
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_AUTO_APPROVE, 'UNSELECTED_GROUP_LABEL'),
      items: unselected,
    },
  ];
});

const adminList = computed<IFormPlainDataListboxItem<number>[]>(() => {
  const language = selectedLanguageId.value;

  if (!language)
    return [];

  const selectedAdmins = props.localizedAdmins?.[language] ?? [];

  return props?.admins?.map(({ id, value }) => ({
    id,
    value,
    check: selectedAdmins.includes(id),
  })) ?? [];
});

function clearLocaleSearchQuery(): void {
  localeFilterQuery.value = '';
}

function onUpdateAdmins(data: IFormPlainDataListboxPropsChangeState<number>) {
  if (selectedLanguageId.value) {
    emit('set-admin', {
      ...props.localizedAdmins,
      [selectedLanguageId.value]: data.values.map(({ id }) => id),
    });
  }
}

watchImmediate(() => props.locales, (newValue) => {
  if (newValue) {
    selectedLanguageId.value = selectedLanguageId.value ?? newValue[0].id;
  }
});
</script>

<template>
  <div v-data-qa-auto-id="'AutoApproveFilter'" class="max-w-[800px]">
    <VSplitter
      :left-prime-vue-props="{ size: 35, minSize: 30 }"
      :left-prime-vue-pt="{
        root: {
          class: 'border-r',
        },
      }"
      :right-prime-vue-props="{ size: 65, minSize: 50 }"
      :right-prime-vue-pt="{
        root: {
          class: 'border-0',
        },
      }"
      :splitter-root-pt="{
        root: {
          class: 'rounded-b-none',
        },
      }"
    >
      <template #left="{ openItem }">
        <PListbox
          v-model="selectedLanguageId"
          :options="groupedLanguages"
          option-label="value"
          option-value="id"
          scroll-height="240px"
          data-key="id"
          :option-group-label="filterAdminId ? 'label' : undefined"
          :option-group-children="filterAdminId ? 'items' : undefined"
          :pt="listboxPt"
          class="w-full border-none"
        >
          <template #header>
            <div class="px-4 py-3">
              <PIconField>
                <PInputIcon class="pi pi-search" />
                <PInputText
                  v-model="localeFilterQuery"
                  :placeholder="$getTranslation(TRANSLATION_GROUP_DROPDOWN, 'PLACEHOLDER')"
                  autocomplete="off"
                  class="w-full"
                />
                <PInputIcon
                  v-if="localeFilterQuery"
                  class="pi pi-times cursor-pointer"
                  @click="clearLocaleSearchQuery"
                />
              </PIconField>
            </div>
          </template>
          <template #option="{ option }">
            <div
              class="relative flex w-full flex-row flex-nowrap items-center justify-between"
              @click="openItem(option.value)"
            >
              <span class="grow p-1">
                {{ option.value }}
              </span>
              <span class="flex items-center gap-3 pr-1">
                <PBadge
                  :value="localizedAdmins?.[option.id]?.length ?? 0"
                  severity="secondary"
                />
                <i class="pi pi-angle-right" />
              </span>
            </div>
          </template>
        </PListbox>
      </template>
      <template #right>
        <VFormPlainDataListbox
          v-if="selectedLanguageId"
          :data="adminList"
          :no-border="isDesktop"
          :filter-query="filteredSearchAdmin"
          :disable-checkbox-filter="!!filteredSearchAdmin"
          :readonly="readonly"
          hide-errors
          is-full-width
          @change="onUpdateAdmins"
        />
      </template>
    </VSplitter>
  </div>
</template>
